import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Menu, X, Phone, ChevronDown } from "lucide-react";
import { Logo } from "./Logo";
import { vaiA } from "@/lib/scroll";
import { TELEFONO, TELEFONO_HREF } from "@/data/citta";
import { MENU_SERVIZI, SERVIZIO_SELECT_MAP, slugifyVoce } from "@/data/menu";

const VOCI_PRIMA = [{ label: "Servizi", hash: "#servizi" }];
const VOCI_DOPO = [
  { label: "Dove interveniamo", hash: "#citta" },
  { label: "Come funziona", hash: "#come-funziona" },
  { label: "FAQ", hash: "#faq" },
  { label: "Contatti", hash: "#contatti" },
];

export function Header() {
  const [aperto, setAperto] = useState(false);
  const [espanso, setEspanso] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    document.body.style.overflow = aperto ? "hidden" : "";
    return () => (document.body.style.overflow = "");
  }, [aperto]);

  const vai = (hash) => {
    setAperto(false);
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => vaiA(hash), 400);
    } else {
      vaiA(hash);
    }
  };

  const navigaPagina = (slug) => {
    setAperto(false);
    navigate(`/servizi/${slug}`);
  };

  const scegliServizio = (categoria, voce) => {
    setAperto(false);
    if (categoria.pagine && categoria.pagine[voce]) {
      navigate(categoria.pagine[voce]);
      return;
    }
    const lancia = () => {
      vaiA("#contatti");
      window.dispatchEvent(
        new CustomEvent("precompila-servizio", {
          detail: { servizio: SERVIZIO_SELECT_MAP[categoria.slug], voce },
        })
      );
    };
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(lancia, 500);
    } else {
      lancia();
    }
  };

  const voceCls =
    "text-sm font-medium text-slate-300 transition-colors hover:text-amber-400";

  return (
    <header className="fixed inset-x-0 top-0 z-40 glass-panel border-x-0 border-t-0">
      <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-5 md:px-8">
        <Logo onClick={() => (location.pathname !== "/" ? navigate("/") : vaiA("#top"))} />
        <nav className="hidden items-center gap-5 lg:flex" aria-label="Navigazione principale">
          {VOCI_PRIMA.map((v) => (
            <button
              key={v.hash}
              type="button"
              data-testid={`nav-${v.hash.slice(1)}`}
              onClick={() => vai(v.hash)}
              className={voceCls}
            >
              {v.label}
            </button>
          ))}
          {MENU_SERVIZI.map((m) => (
            <div key={m.slug} className="group relative">
              <button
                type="button"
                data-testid={`nav-${m.slug}`}
                onClick={() => navigaPagina(m.slug)}
                className={`${voceCls} flex items-center gap-1`}
                aria-haspopup="true"
              >
                {m.nome}
                <ChevronDown
                  size={14}
                  className="transition-transform duration-200 group-hover:rotate-180"
                />
              </button>
              <div className="invisible absolute left-1/2 top-full z-50 w-64 -translate-x-1/2 pt-3 opacity-0 transition-all duration-200 group-hover:visible group-hover:opacity-100">
                <div className="glass-panel rounded-xl p-2">
                  {m.voci.map((v) => (
                    <button
                      key={v}
                      type="button"
                      data-testid={`nav-${m.slug}-${slugifyVoce(v)}`}
                      onClick={() => scegliServizio(m, v)}
                      className="block w-full rounded-lg px-4 py-2.5 text-center text-sm text-slate-300 transition-colors hover:bg-slate-800 hover:text-amber-400"
                    >
                      {v}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          ))}
          {VOCI_DOPO.map((v) => (
            <button
              key={v.hash}
              type="button"
              data-testid={`nav-${v.hash.slice(1)}`}
              onClick={() => vai(v.hash)}
              className={voceCls}
            >
              {v.label}
            </button>
          ))}
        </nav>
        <div className="flex items-center gap-3">
          <a
            href={TELEFONO_HREF}
            data-testid="cta-chiama-ora-header"
            className="hidden items-center gap-2 rounded-full bg-amber-500 px-5 py-2.5 text-sm font-bold text-obsidian transition-all hover:bg-amber-400 hover:shadow-[0_0_28px_rgba(245,158,11,0.35)] xl:inline-flex"
          >
            <Phone size={16} strokeWidth={2.5} />
            Chiama ora · {TELEFONO}
          </a>
          <button
            type="button"
            data-testid="menu-mobile-toggle"
            onClick={() => setAperto(!aperto)}
            className="inline-flex rounded-lg border border-slate-700 p-2.5 text-slate-200 lg:hidden"
            aria-label={aperto ? "Chiudi menu" : "Apri menu"}
          >
            {aperto ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>
      {aperto && (
        <div
          className="glass-panel absolute inset-x-0 top-[72px] max-h-[calc(100vh-72px)] overflow-y-auto border-x-0 lg:hidden"
          data-testid="menu-mobile-panel"
        >
          <nav className="flex flex-col gap-1 p-5 text-center" aria-label="Navigazione mobile">
            {VOCI_PRIMA.map((v) => (
              <button
                key={v.hash}
                type="button"
                data-testid={`nav-mobile-${v.hash.slice(1)}`}
                onClick={() => vai(v.hash)}
                className="rounded-lg px-4 py-3.5 text-base font-medium text-slate-200 transition-colors hover:bg-slate-800 hover:text-amber-400"
              >
                {v.label}
              </button>
            ))}
            {MENU_SERVIZI.map((m) => (
              <div key={m.slug}>
                <button
                  type="button"
                  data-testid={`nav-mobile-${m.slug}`}
                  onClick={() => setEspanso(espanso === m.slug ? null : m.slug)}
                  className="flex w-full items-center justify-center gap-1.5 rounded-lg px-4 py-3.5 text-base font-medium text-slate-200 transition-colors hover:bg-slate-800 hover:text-amber-400"
                  aria-expanded={espanso === m.slug}
                >
                  {m.nome}
                  <ChevronDown
                    size={16}
                    className={`transition-transform ${espanso === m.slug ? "rotate-180" : ""}`}
                  />
                </button>
                {espanso === m.slug && (
                  <div className="mx-auto mb-1 max-w-xs border-y border-slate-800 py-1">
                    <button
                      type="button"
                      data-testid={`nav-mobile-${m.slug}-pagina`}
                      onClick={() => navigaPagina(m.slug)}
                      className="block w-full rounded-lg px-4 py-2.5 text-sm font-semibold text-amber-400 transition-colors hover:text-amber-300"
                    >
                      Pagina {m.nome} →
                    </button>
                    {m.voci.map((v) => (
                      <button
                        key={v}
                        type="button"
                        data-testid={`nav-mobile-${m.slug}-${slugifyVoce(v)}`}
                        onClick={() => scegliServizio(m, v)}
                        className="block w-full rounded-lg px-4 py-2.5 text-sm text-slate-400 transition-colors hover:text-amber-400"
                      >
                        {v}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
            {VOCI_DOPO.map((v) => (
              <button
                key={v.hash}
                type="button"
                data-testid={`nav-mobile-${v.hash.slice(1)}`}
                onClick={() => vai(v.hash)}
                className="rounded-lg px-4 py-3.5 text-base font-medium text-slate-200 transition-colors hover:bg-slate-800 hover:text-amber-400"
              >
                {v.label}
              </button>
            ))}
            <a
              href={TELEFONO_HREF}
              data-testid="cta-chiama-ora-mobile"
              className="mt-3 inline-flex items-center justify-center gap-2 rounded-full bg-amber-500 px-5 py-3.5 text-base font-bold text-obsidian"
            >
              <Phone size={18} strokeWidth={2.5} />
              Chiama ora · {TELEFONO}
            </a>
          </nav>
        </div>
      )}
    </header>
  );
}
